import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import config from '../config';

const AnalyticsTracker = () => {
  const location = useLocation();
  const sessionId = useRef(null);
  const visitStartTime = useRef(null);
  const pageViews = useRef(0);
  const lastPageUrl = useRef('');
  const [country, setCountry] = useState(null);

  // Generate a unique session ID
  const generateSessionId = () => {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  // Get screen resolution
  const getScreenResolution = () => {
    return `${window.screen.width}x${window.screen.height}`;
  };

  // Get browser language
  const getLanguage = () => {
    return navigator.language || navigator.userLanguage || 'en-US';
  };

  // Get referrer
  const getReferrer = () => {
    return document.referrer || null;
  };

  // Detect device type
  const getDeviceType = () => {
    const userAgent = navigator.userAgent;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const tabletRegex = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i;

    if (tabletRegex.test(userAgent)) return 'tablet';
    if (mobileRegex.test(userAgent)) return 'mobile';
    return 'desktop';
  };

  // Generate or get deviceId from localStorage
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  };

  // Send analytics data to backend
  const sendAnalytics = async data => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/analytics/visit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error('Failed to send analytics data');
      }
    } catch (error) {
      console.error('Error sending analytics data:', error);
    }
  };

  // Record page view
  const recordPageView = useCallback(() => {
    const currentUrl = window.location.href;
    const pageTitle = document.title;

    // Don't record if it's the same page (just a hash change)
    if (currentUrl === lastPageUrl.current) {
      return;
    }

    pageViews.current++;
    lastPageUrl.current = currentUrl;

    const analyticsData = {
      deviceId: getDeviceId(),
      sessionId: sessionId.current,
      pageUrl: currentUrl,
      pageTitle: pageTitle,
      screenResolution: getScreenResolution(),
      language: getLanguage(),
      referrer: getReferrer(),
      pageViews: pageViews.current,
      deviceType: getDeviceType(),
      country: country || undefined,
    };

    sendAnalytics(analyticsData);
  }, [country]); // No dependencies because all used values are refs or functions

  // Record visit duration when user leaves
  const recordVisitDuration = useCallback(() => {
    if (visitStartTime.current) {
      const duration = Math.floor((Date.now() - visitStartTime.current) / 1000);

      const analyticsData = {
        deviceId: getDeviceId(),
        sessionId: sessionId.current,
        pageUrl: window.location.href,
        pageTitle: document.title,
        screenResolution: getScreenResolution(),
        language: getLanguage(),
        referrer: getReferrer(),
        pageViews: pageViews.current,
        deviceType: getDeviceType(),
        visitDuration: duration,
        country: country || undefined,
      };

      // Send final analytics data
      sendAnalytics(analyticsData);
    }
  }, [country]); // No dependencies because all used values are refs or functions

  // Initialize analytics tracking
  useEffect(() => {
    // Generate session ID if not exists
    if (!sessionId.current) {
      sessionId.current = generateSessionId();
      visitStartTime.current = Date.now();
    }

    // Record initial page view
    recordPageView();

    // Set up beforeunload event to record visit duration
    const handleBeforeUnload = () => {
      recordVisitDuration();
    };

    // Set up visibility change event to track when user leaves the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        recordVisitDuration();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [recordPageView, recordVisitDuration]);

  // Fetch country from geolocation API on mount
  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        if (data && data.country) setCountry(data.country);
      })
      .catch(() => setCountry(null));
  }, []);

  // Record page view when location changes
  useEffect(() => {
    // Small delay to ensure the page has loaded
    const timer = setTimeout(() => {
      recordPageView();
    }, 100);

    return () => clearTimeout(timer);
  }, [location, recordPageView]);

  // This component doesn't render anything
  return null;
};

export default AnalyticsTracker;
